import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import MailIcon from '../../assets/svg/MailIcon';
import { CallLogsTable, FiltersPanel } from '../../components/CallLog';
import { SendReportModal } from '../../components/Modals';
import { Button, ButtonExport } from '../../components/Shared';
import {
  CALL_DURATION,
  DATE_TIME_FORMAT,
  REPORT_FILE_FORMAT,
} from '../../constants';
import {
  downloadFile,
  getFormattedAdminTeamFilter,
  getFormattedDateInMomentDefaultTZ,
  getNowInDefaultTimezoneAsDate,
} from '../../helpers';
import { Layout } from '../../layouts';

const getFormattedFilters = filters => {
  return {
    search: filters.search,
    adminTeam: getFormattedAdminTeamFilter(filters.callerIds),
    startDate: getFormattedDateInMomentDefaultTZ(filters.startDate).format(
      DATE_TIME_FORMAT.M_D_YYYY,
    ),
    endDate: getFormattedDateInMomentDefaultTZ(filters.endDate).format(
      DATE_TIME_FORMAT.M_D_YYYY,
    ),
    callOutcome: filters.callOutcome,
    callDurationFrom: filters.callDuration[0],
    callDurationTo: filters.callDuration[1],
  };
};

export const CallLog = () => {
  const [selectedCallRecordingIDs, setSelectedCallRecordingIDs] = useState(
    new Map(),
  );
  const [isSendReportModalOpened, setIsSendReportModalOpened] = useState(false);

  const openSendReportModal = () => setIsSendReportModalOpened(true);
  const closeSendReportModal = () => setIsSendReportModalOpened(false);

  const initialFilters = {
    search: '',
    callerIds: [],
    callDuration: [CALL_DURATION.MIN, CALL_DURATION.MAX],
    startDate: getNowInDefaultTimezoneAsDate(),
    endDate: getNowInDefaultTimezoneAsDate(),
    callOutcome: null,
  };

  const [filters, setFilters] = useState(initialFilters);

  const formattedFilters = useMemo(
    () => getFormattedFilters(filters),
    [filters],
  );

  const resetFilters = () => {
    setFilters(initialFilters);
  };

  const abortControllerRef = useRef(null);
  const [callLogs, setCallLogs] = useState([]);
  const [isCallLogsLoading, setIsCallLogsLoading] = useState(true);
  const [isCallLogsError, setIsCallLogsError] = useState(false);

  const fetchCallLogs = useCallback(async () => {
    setIsCallLogsLoading(true);
    setIsCallLogsError(false);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const {
      search,
      adminTeam,
      startDate,
      endDate,
      callOutcome,
      callDurationFrom,
      callDurationTo,
    } = formattedFilters;

    try {
      const response = await fetch('/admin/call-logs', {
        signal: abortControllerRef.current.signal,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          startDate,
          endDate,
          ...(!!search && { search }),
          ...(!!adminTeam && { adminTeam }),
          ...(!!callOutcome && { callOutcome }),
          ...(callDurationFrom !== CALL_DURATION.MIN && { callDurationFrom }),
          ...(callDurationTo !== CALL_DURATION.MAX && { callDurationTo }),
        }),
      });

      if (response.status !== 200) throw new Error();

      const data = await response.json();
      const callLogs = data.callRecordings.query1;
      setIsCallLogsLoading(false);
      setSelectedCallRecordingIDs(prev => {
        const callRecordingIDs = new Set(
          callLogs.map(({ CallRecordingID }) => CallRecordingID),
        );
        const newSelectedCallRecordingIDs = new Map(prev);

        prev.forEach(id => {
          const isSelected = callRecordingIDs.has(id);

          if (!isSelected) {
            newSelectedCallRecordingIDs.delete(id);
          }
        });

        return newSelectedCallRecordingIDs;
      });
      setCallLogs(callLogs);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      setIsCallLogsLoading(false);
      setIsCallLogsError(true);
    }
  }, [formattedFilters]);

  useEffect(() => {
    fetchCallLogs();
  }, [fetchCallLogs]);

  const [isExportReportsSubmitting, setIsExportReportsSubmitting] =
    useState(false);

  const exportReports = async reportFormat => {
    setIsExportReportsSubmitting(true);

    try {
      const response = await fetch('/admin/export-call-logs-report-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportFormat,
          callRecordingIDs: [...selectedCallRecordingIDs.keys()].join(';'),
        }),
      });

      if (response.status !== 200) throw new Error();

      if (reportFormat === REPORT_FILE_FORMAT.XLSX) {
        const blob = await response.blob();
        downloadFile(blob, 'Call Log.xlsx');
      }

      if (reportFormat === REPORT_FILE_FORMAT.CSV) {
        const blob = await response.blob();
        downloadFile(blob, 'Call Log.csv');
      }

      setIsExportReportsSubmitting(false);
    } catch {
      setIsExportReportsSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const [isSendReportsSubmitting, setIsSendReportsSubmitting] = useState(false);

  const sendReports = async data => {
    setIsSendReportsSubmitting(true);

    try {
      const response = await fetch('/admin/send-call-logs-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          callRecordingIDs: [...selectedCallRecordingIDs.keys()].join(';'),
        }),
      });

      if (response.status !== 200) throw new Error();

      setIsSendReportsSubmitting(false);
      closeSendReportModal();
    } catch {
      setIsSendReportsSubmitting(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const isFiltersChanged = !isEqual(
    getFormattedFilters(initialFilters),
    formattedFilters,
  );
  const areReportActionsDisabled =
    selectedCallRecordingIDs.size === 0 || isCallLogsLoading;
  const isButtonExportDisabled =
    areReportActionsDisabled || isExportReportsSubmitting;
  const isButtonSendReportDisabled = areReportActionsDisabled;
  const numberCallLogs = callLogs.length;
  const hasCallLogs = numberCallLogs > 0;
  const numberSelectedCallLogs = selectedCallRecordingIDs.size;
  const hasSelectedCallLogs = numberSelectedCallLogs > 0;

  return (
    <Layout>
      <div className="flex flex-col gap-4 h-full overflow-hidden">
        <div className="flex justify-between gap-3 overflow-auto shrink-0">
          <div className="flex gap-3 shrink-0">
            <FiltersPanel
              initialCallDuration={initialFilters.callDuration}
              filters={filters}
              onChangeFilters={setFilters}
            />
            {isFiltersChanged && (
              <Button
                title="Clear All"
                variant="text"
                onClick={resetFilters}
                className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
              />
            )}
          </div>
          <div className="flex gap-3 shrink-0">
            {hasCallLogs && (
              <div className="flex items-center gap-1">
                <span className="text-base-1000 font-medium text-[16px]/[24px]">
                  {hasSelectedCallLogs
                    ? `${numberSelectedCallLogs}/${numberCallLogs}`
                    : numberCallLogs}
                </span>
                <span className="text-base-1000 text-[16px]/[24px]">
                  {hasSelectedCallLogs ? 'selected' : 'records'}
                </span>
              </div>
            )}
            <ButtonExport
              disabled={isButtonExportDisabled}
              onClick={exportReports}
            />
            <Button
              title="Send Report"
              variant="blue"
              iconStart={
                <MailIcon
                  className={twMerge(
                    'w-5 h-5 text-brand-900',
                    isButtonSendReportDisabled && 'text-base-400',
                  )}
                />
              }
              disabled={isButtonSendReportDisabled}
              onClick={openSendReportModal}
            />
          </div>
        </div>
        <CallLogsTable
          callLogs={callLogs}
          selectedCallRecordingIDs={selectedCallRecordingIDs}
          isCallLogsLoading={isCallLogsLoading}
          isCallLogsError={isCallLogsError}
          fetchCallLogs={fetchCallLogs}
          onChangeSelectedCallRecordingIDs={setSelectedCallRecordingIDs}
        />
        {isSendReportModalOpened && (
          <SendReportModal
            open={isSendReportModalOpened}
            disabled={isSendReportsSubmitting}
            onSubmit={sendReports}
            onClose={closeSendReportModal}
          />
        )}
      </div>
    </Layout>
  );
};
